import {React} from '@api'
import {CustomAttributeDefinition} from '@api/graphql'
import {ModalConfig, FieldType, ModalSectionRows, WorkspaceConfig, CatalogImageSourceConfig} from '@stores/config'
import {IUser} from '@stores/user'
import {IUserInfo} from '@utils'
import NumberFormatOptions = Intl.NumberFormatOptions
import {Stores, StoresInstance} from '@stores'
import { ModalSecondariesTemplate, ModalSectionType } from './modal'
export * from './modal'

export type EsIndexName = string
export type DetailViewsConfig = Record<EsIndexName, DetailsViewUiConfig>

export interface DetailsViewUiConfig {
  imageSources?: Array<CatalogImageSourceConfig>

  /**
   * For modal details
   */
  modal?: ModalConfig

  /**
   * For sidebar (library) details
   */
  sidebar?: SidebarConfig
}

export interface SidebarConfig {
  sections: Array<DetailsViewSectionConfig>
  header?: DetailsViewHeaderConfig
}

export interface DetailsViewHeaderConfig {
  buttons?: Array<DetailsViewButtonConfig>
}

export type DetailsViewButtonType = 'duplicate'

export interface DetailsViewButtonConfig {
  label?: string
  type: DetailsViewButtonType
}

export interface DetailsViewSectionConfig {
  id?: any
  name?: string
  fields?: Array<DetailsViewFieldConfig>
  style?: React.CSSProperties
  
  type?: string | ModalSectionType
  secondariesTemplate?: ModalSecondariesTemplate
  columns?: number
  field?: DetailsViewFieldConfig
  defaultCollapsed?: boolean
  /**
   * If specified we can drag objects of this type on to this section to assign the object (say style -> mpv)
   */
  dropTarget?: { field?: string }
  noFill?: boolean // Don't fill in the grid (mixing and matching manually spanning columns and not)
  label?: string
  countField?: string

  /**
   * Don't actually display these on the main card for the board, show it when drilled down into a nested card
   */
  nested?: boolean

  rows?: ModalSectionRows
  itemSource?: string
}

export type FieldRendererType = 'card' | 'chip' | 'toggleList' | 'text' | 'cardGroup' | 'percentage' | 'html' | 'dropdown' | 'seasonSelector' | 'headerCustomBoolean' | 'headerCustomEnumType' | 'headerCustomString' | 'table'
export type DetailsFieldType = 'sync'
  | 'seasonSelector'
  | 'button'
  | 'text'
  | 'plmLink'
  | 'date'
  | 'xref'
  | 'editableText'
  | 'numeric'
  | 'plmHyperLink'
  | 'html'
  | '3d-emersya'
  | 'cards'
  | 'images'
  | 'inline-images'
  | 'Hex'
  | 'bom' | 'bom-usages'
  | 'comments'
  | 'inline'
  | 'headerCustomBoolean'
  | 'adoption'
  | 'specCard'
  | 'arrXref'
  | 'annotate-images'

export type IEditableParams = { data?: any, stores?: Partial<Stores>, workspace?: Partial<WorkspaceConfig>, field?: any, secondary?: any, isPublished?: boolean }
export type IEditableCallback = ((params: IEditableParams) => boolean) | boolean

export interface DetailsViewFieldConfig {
  editable?: boolean | ((...args: any[]) => any)
  baseNode?: string
  /**
   * Hardcoded value for the field
   */
  value?: any
  hasSeparator?: boolean
  label?: string | boolean,
  field?: string,
  section?: string

  /** Usually there will be a catalog field with the type, but some fields are detail pane only like comments and change history **/
  type?: DetailsFieldType

  def?: CustomAttributeDefinition,
  fieldGroup?: string[]
  library?: string
  nested?: {
    /** This is relative to the parent object, not the overall catalog index row
     * as we can have something like .styles.colorways and the colorways to be shown in a nested are _for a given style instance_
     * */
    field: string
    label?: string
    dataMap?: any
    hideActions?: boolean
    library?: string
  }

  // Used for 'cards' type
  titleField?: string
  fields?: Array<DetailsViewFieldConfig> | ((data: any, stores: StoresInstance) => Array<DetailsViewFieldConfig>)

  has3d?: boolean

  /**
   * Subfield with the 3d ID
   */
  field3d?: string
  screenshotsField?: string

  bom?: BillOfMaterialsConfig

  /**
   * Associated workspace for renderer, used by bom-details
   */
  workspace?: string

  computed?: DetailsFieldComputedType

  headerLabel?: boolean
  /**
   * Section Modal Rows in the section
   */
  rows?: ModalSectionRows

  agg?: string
  /**
   * to show only label in the section
   */
  labelOnly?: boolean

  header?: DetailViewHeaderFieldView,

  onlyUIField?: boolean

  assignToXrefField?: string

  /** To fine-tune L&F in the modal grids **/
  rowSpan?: number
  colSpan?: number
}

export interface DetailViewHeaderFieldView {
  field?: string
  path?: string
}

export type DetailsFieldComputedType = {
  expression?: DetailsFieldComputedExpressionType
}

export type DetailsFieldComputedExpressionType = {
  field?: string,
  fields?: Array<string>,
  path?: string
  compute?: any
}

export type DetailsFieldAssignToType = {
  expression?: DetailsFieldAssignToExpressionType
}

export type DetailsFieldAssignToExpressionType = {
  field?: string,
  path?: string
  assignTo?: any
  computeField?: string
}

export type DetailsFieldBackgroundColor = {
  value?: string,
  background: string,
  color?: string
}

export interface BOMSpecConfig {
  field: string
  bomFields: string[]
  rollups: Array<BillOfMaterialsRollupConfig>
}

export interface BillOfMaterialsConfig {
  specs: Array<BOMSpecConfig>
}

export interface BillOfMaterialsRollupConfig {
  localOnly: boolean
  label: string,
  localFields?: Array<string>
  specFields?: Array<string>
  prefix?: string
  precision?: number
  rollup: (fields: {[field: string]: any}, quantity: number) => number
}

export type IOverviewTableCatalogItemDetailTemplate = {
  component: 'overview-grid',
  layout: {
    upperLeft?: Array<IOverviewTableCatalogItemDetailTemplateField>
    upperRight?: Array<IOverviewTableCatalogItemDetailTemplateField>
    rows?: Array<IOverviewTableCatalogItemDetailTemplateRow>
  }
}

export type IOverviewTableCatalogItemDetailTemplateField = {
  field?: string
  agg?: 'acount'
}

export type IOverviewTableCatalogItemDetailTemplateColumn = IOverviewTableCatalogItemDetailTemplateField

export type IOverviewTableCatalogItemDetailTemplateRow = {
  columns?: Array<IOverviewTableCatalogItemDetailTemplateColumn>
  type?: 'images', heroImage?: { field: string }
}

export type ICatalogItemDetailTemplate = IOverviewTableCatalogItemDetailTemplate


// WEBPACK FOOTER //
// ./src/stores/config/deployment/detailsView/ui.ts